import { HelpOutline } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import { Box, InputBase, Tooltip } from '@mui/material';
import { styled, SxProps, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useDraggedTextareaHeight } from '@/hooks/useDraggedTextareaHeight';
import { BodyText } from '../BodyText';
import { RoundedSquareIconButton } from '../RoundedSquareIconButton';

const SearchBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '6px',
}));

const StyledSearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  alignItems: 'flex-end',
  padding: '8px 8px 2px 12px',
  '& textarea:not([aria-hidden])': {
    minHeight: '1lh',
    resize: 'vertical',
    overflowY: 'auto !important',
  },
  '& textarea::placeholder': {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
}));

const StyledSearchIcon = styled(SearchIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const SearchTooltip = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        paddingTop: '6px',
        flexDirection: 'column',
        gap: '2px',
        padding: '0 0 4px 0',
      }}
    >
      <BodyText>Keywords match filename and metadata.</BodyText>
      <BodyText>&quot;Keywords in quotes&quot; require an exact match.</BodyText>
    </Box>
  );
};

export const SearchInput = ({
  initialValue,
  onSearch,
  placeholder = 'Search...',
  AddToSearchButton,
  SearchAttachment,
  sx,
}: {
  initialValue: string;
  onSearch: (query: string) => void;
  AddToSearchButton?: React.ReactNode;
  SearchAttachment?: React.ReactNode;
  placeholder?: string;
  sx?: SxProps;
}) => {
  const theme = useTheme();
  const [value, setValue] = useState(initialValue);
  const [searchEntered, setSearchEntered] = useState(false);
  const { minHeight, rememberHeightIfDragged, forgetDraggedHeight } = useDraggedTextareaHeight();

  useEffect(() => {
    setValue(initialValue);
    if (!initialValue) {
      forgetDraggedHeight();
    }
  }, [initialValue, forgetDraggedHeight]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const query = event.target.value.replaceAll(/[\r\n]+/g, ' ');
    setValue(query);
    if (!query) {
      forgetDraggedHeight();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      handleSearchClick();
    }
  };

  const handleSearchClick = () => {
    onSearch(value);
    setSearchEntered(true);
  };

  const helpIcon = (
    <Tooltip
      title={<SearchTooltip />}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: '600px !important',
          },
        },
      }}
    >
      <HelpOutline sx={{ fontSize: '17px', margin: '0 5px', cursor: 'default', color: theme.palette.text.secondary }} />
    </Tooltip>
  );

  const submitButton = (
    <Tooltip title='Submit'>
      <RoundedSquareIconButton
        size='small'
        onClick={handleSearchClick}
        sx={{
          margin: '6px',
          background: value?.trim() && !searchEntered ? 'rgba(56, 119, 85, 0.15)' : 'transparent',
        }}
      >
        {value?.trim() ? <SendIcon sx={{ fontSize: '18px' }} /> : <StyledSearchIcon sx={{ fontSize: '18px' }} />}
      </RoundedSquareIconButton>
    </Tooltip>
  );

  return (
    <SearchBox sx={sx}>
      <StyledSearchInput
        multiline
        minRows={1}
        slotProps={{ input: { onPointerDown: rememberHeightIfDragged, style: { minHeight } } }}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        fullWidth
        autoFocus
        onBlur={handleSearchClick}
        endAdornment={
          AddToSearchButton ? (
            helpIcon
          ) : (
            <Box display='flex' flexDirection='row' alignItems='center'>
              {helpIcon}
              {submitButton}
            </Box>
          )
        }
      />
      {SearchAttachment && (
        <Box display='flex' width='100%' flexDirection='row' alignItems='center' justifyContent='flex-start'>
          {SearchAttachment}
        </Box>
      )}
      {AddToSearchButton && (
        <Box display='flex' width='100%' flexDirection='row' alignItems='center' justifyContent='space-between'>
          {AddToSearchButton}
          {submitButton}
        </Box>
      )}
    </SearchBox>
  );
};
